<template>
  <div class="modal-overlay" @click.self="handleClose(false)">
    <div class="modal-content">
      <div class="modal-header">
        <div
          class="d-flex align-items-center justify-content-start w-80 search-content"
        >
          <!-- SearchBar -->
          <div class="search-bar">
            <input
              autocomplete="off"
              id="search-input-custom"
              ref="searchInput"
              type="text"
              v-model="searchText"
              @input="handleInput"
              placeholder="Type to search..."
              class="search-input"
              @keydown="handleKeyDown"
              @focus="handleFocus"
            />
          </div>
          <select
            id="skin-select"
            v-model="skinFilt"
            v-if="!isSkinOnStorage"
            class="select-custom select-custom-2 skin-select"
          >
            <option value="Choose Skin" disabled>Choose Skins</option>
            <option
              v-for="skin in sortedAllowedSkins"
              :key="skin._id"
              :value="skin.ref_id"
            >
              {{ skin.ref_id }}
            </option>
          </select>
          <i class="fas fa-search search-icon" @click="handleSearchClick"></i>
        </div>

        <button class="close-btn" @click="handleClose(false)">
          <i class="fas fa-times"></i>
          ESC
        </button>
      </div>
      <div class="tags-input-wrapper">
        <div class="tags-input">
          <div
            v-for="(tag, index) in tags"
            :key="'tag-' + index"
            class="tag"
            :class="tag.type"
          >
            <div
              class="d-flex align-items-center justify-content-start tag-content-wrapper"
            >
              <span class="tag-text">{{ tag.text }}</span>
              <div class="custom-dropdown" @click.stop="toggleDropdown(index)">
                <div class="selected-option">
                  <i class="fas" :class="getTagIcon(tag.type)"></i>
                </div>
                <div v-if="activeDropdown === index" class="dropdown-options">
                  <div
                    v-for="type in tagTypes"
                    :key="type"
                    :disabled="tag.type === type"
                    class="dropdown-option"
                    :class="tag.type === type ? 'disabled-type' : ''"
                    @click="handleTagTypeChange(index, type)"
                  >
                    <i class="fas me-2" :class="getTagIcon(type)"></i>
                    <p class="text-slate-700 mb-0 small text-capitalize">
                      {{ type.replaceAll("_", " ") }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <button @click="removeTag(index)" class="remove-btn">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div v-if="addTagError !== ''" class="tag-error">
          <i class="fa-solid fa-circle-exclamation ms-1 me-2"></i>
          {{ addTagError }}
        </div>
      </div>

      <div v-if="noResult && !isLoading" class="no-result-message">
        <i class="fas fa-exclamation-circle me-2"></i>
        No result found for this search
      </div>
      <div v-if="isLoading" class="loader-overlay">
        <Loader />
      </div>
      <div
        v-if="tags && tags?.length > 0"
        class="clear-all-tags-btn"
        @click="clearAllTags"
      >
        Clear all tags
      </div>

      <!-- SearchResults -->
      <div class="search-results">
        <div v-if="recentSearches.length" class="section">
          <h3 class="pb-1">
            <i class="fas fa-history"></i>
            Recent Searches
          </h3>
          <div
            v-for="(search, index) in recentSearches"
            :key="'recent-' + index"
            :class="['result-item', { selected: isSelected(index) }]"
            @click="searchFromString(recentSearches[index])"
          >
            <span
              class="d-flex flex-wrap gap-2 align-items-center justify-content-start"
              v-html="formatSearchString(search)"
            ></span>
            <button
              @click.stop="toggleFavorite(search)"
              :class="['favorite-btn', { active: favorites.includes(search) }]"
            >
              <i class="fas fa-star"></i>
            </button>
          </div>
        </div>

        <div v-if="favorites.length" class="section">
          <h3 class="pb-1">
            <i class="fas fa-star"></i>
            Favorites
          </h3>
          <div
            v-for="(favorite, index) in favorites"
            :key="'fav-' + index"
            :class="[
              'result-item',
              { selected: isSelected(index + recentSearches.length) },
            ]"
            @click="searchFromString(favorite)"
          >
            <span
              class="d-flex flex-wrap gap-2 align-items-center justify-content-start"
              v-html="formatSearchString(favorite)"
            ></span>
            <button
              @click.stop="toggleFavorite(favorite)"
              class="favorite-btn active"
            >
              <i class="fas fa-star"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="modal-footer justify-content-between">
        <div class="shortcut over-info" @click="startTutorial">
          <i class="fas fa-info-circle"></i>
          How to use
        </div>
        <div class="d-flex align-items-center">
          <div class="shortcut">
            <i class="fas fa-star"></i>
            Toggle favorite
          </div>
          <div class="shortcut mx-1">
            <span class="key">↵</span>
            Select
          </div>
          <div class="shortcut">
            <span class="key">ESC</span>
            Close
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Tutorial Modal -->
  <div v-if="showTutorial" class="tutorial-overlay" @click.self="closeTutorial">
    <div class="tutorial-content">
      <div class="tutorial-header">
        <h2>
          <i class="fas fa-graduation-cap me-2"></i>
          How to Use Global Search
        </h2>
        <button class="tutorial-close-btn" @click="closeTutorial">
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="tutorial-body">
        <div
          class="tutorial-step"
          v-for="(step, index) in tutorialSteps"
          :key="index"
        >
          <div class="step-number">{{ index + 1 }}</div>
          <div class="step-content">
            <h3>
              <i
                class="fas"
                :class="[
                  step.icon,
                  step.icon === 'fa-star' ? 'star-yellow' : '',
                ]"
              ></i>
              {{ step.title }}
            </h3>
            <p>{{ step.description }}</p>

            <div v-if="step.examples" class="examples">
              <h4>Examples:</h4>
              <div
                :class="
                  index === 2
                    ? 'd-flex align-items-center flex-wrap justify-content-start pt-2'
                    : ''
                "
              >
                <div
                  v-for="example in step.examples"
                  :key="example.text"
                  class="example"
                  :class="index === 2 ? `wrap-type ${example.text}` : ''"
                >
                  <i
                    v-if="index === 2"
                    class="fas me-2"
                    :class="getTagIcon(example.text)"
                  ></i>
                  <code v-if="index !== 2">{{ example.text }}</code>
                  <div v-else class="text-white text-capitalize">
                    {{ example.text.replaceAll("_", " ") }}
                  </div>
                  <span class="example-desc">{{ example.description }}</span>
                </div>
              </div>
            </div>
            <small class="text-warning small fw-bold my-2">{{
              step.additional_notes
            }}</small>
          </div>
        </div>
      </div>

      <div class="tutorial-footer">
        <button class="tutorial-btn secondary" @click="closeTutorial">
          Got it!
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import store from "./searchStore";
import { getFetch, postFetch } from "../../api/axiosClient";
import Loader from "../Loader.vue";
import { mapGetters } from "vuex";
export default {
  name: "SearchModalFull",
  components: {
    Loader,
  },
  data() {
    return {
      searchText: "",
      tagTypes: ["bet_id", "round_id", "user_id"],
      tagTypeErrors: {},
      addTagError: "",
      errorTimeout: null,
      noResultTimeout: null,
      activeDropdown: null,
      skinFilt: false,
      isSkinOnStorage: false,
      noResult: false,
      isLoading: false,
      recentSearches: [],
      favorites: [],
      showTutorial: false,
      tutorialSteps: [
        {
          icon: "fa-search",
          title: "Search",
          description:
            "Type any text to search across all fields. The search is performed in real-time as you type.",
          examples: [
            {
              text: "9999-Test",
              description:
                'Searches for USER ID, for example "9999_test" in all fields.',
            },
            {
              text: "b72f348c-961a-1234-88f8-a08fd0bf45cd",
              description: "Searches for Round ID.",
            },
            {
              text: "D_sda4234erwr942302",
              description: "Searches for BET ID.Always start with 'D_'.",
            },
          ],
        },
        {
          icon: "fa-tags",
          title: "Using tags",
          description:
            "Use tags to create specific search filters. Press Space to create a tag from your input.",
          examples: [
            {
              text: "User ID: 12345",
              description: "Search for specific USER ID.",
            },
            {
              text: "Round ID: b72f348c-961a-1234-88f8-a08fd0b999cd",
              description: "Search for specific round ID.",
            },
            {
              text: "Bet ID: D_424324fsfsdvasd",
              description: "Filter by BET ID.",
            },
          ],
          additional_notes:
            "Once you have entered an input, performed a search, or pressed the TAB key to add a new tag, the tag type will be automatically assigned.",
        },
        {
          icon: "fa-sliders-h",
          title: "Tag Types",
          description:
            "Click on the tag icon to change the filter type. Different types apply different search logic.",
          additional_notes:
            "You can only use one tag per type, and up to 3 tags at a time during a search.",
          examples: [
            {
              text: "user_id",
            },
            {
              text: "round_id",
            },
            {
              text: "bet_id",
            },
          ],
        },
        {
          icon: "fa-star",
          title: "Favorites & History",
          description:
            "Save frequently used searches as favorites and access your recent searches quickly.",
        },
        {
          icon: "fa-keyboard",
          title: "Keyboard Shortcuts",
          description:
            "Use keyboard shortcuts for faster navigation: Enter to select, ESC to close, Tab to add new tag for your search.",
        },
      ],
    };
  },
  computed: {
    tags() {
      return store.state.tags;
    },
    sortedAllowedSkins() {
      return [...this.allowedSkins].sort((a, b) => a.ref_id - b.ref_id);
    },

    recentSearches() {
      return store.state.recentSearches;
    },
    favorites() {
      return store.state.favorites;
    },
    selectedIndex() {
      return store.state.selectedIndex;
    },
    ...mapGetters([
      "allowedSkins",
      "isAdmin",
      "user_id",
      "isMenuOpen",
      "chatSkinFilter",
    ]),
  },
  methods: {
    startTutorial() {
      this.showTutorial = true;
    },

    closeTutorial() {
      this.showTutorial = false;
    },

    formatSearchString(search) {
      return search
        .split(" ")
        .map(
          (word) =>
            `<span class="${store.getters.getTagType(
              word
            )}-tag-type tag-type-last-favourites">${word}</span>`
        )
        .join(" ");
    },

    updateSkinFilt() {
      if (!this.skinFilt) this.skinFilt = this.allowedSkins[0].ref_id;
    },

    clearAllTags() {
      store.commit("clearTags");
    },

    async fetchLastAndFavourtiesSearch() {
      try {
        const response = await getFetch(
          `users/${this.user_id}`,
          {},
          false,
          true
        );
        let results = response.data;

        if (results && results.search_recents) {
          const recentItems = Object.values(results.search_recents);
          store.commit("addToRecent", recentItems);
        }

        if (results && results.search_favourites) {
          const favItems = Object.values(results.search_favourites);
          store.commit("addToFavourites", favItems);
        }
      } catch (error) {
        console.error(error);
      } finally {
      }
    },
    handleInput(e) {
      const text = e.target.value;
      if (text.endsWith(" ") && text.trim()) {
        this.addTagFromSuggestion(text.trim());
      } else {
        this.searchText = text;
      }
    },
    handleFocus() {
      store.commit("setSelectedIndex", -1);
    },
    handleSearchClick() {
      const event = { key: "Enter", preventDefault: () => {} };
      this.handleKeyDown(event);
    },
    handleKeyDown(e) {
      if (e.key === "Enter") {
        e.preventDefault();

        const allSuggestions = [...this.recentSearches, ...this.favorites];
        const selectedText = allSuggestions[this.selectedIndex];

        let added = false;

        if (this.searchText.trim() === "") {
          if (this.tags.length > 0) {
            const terms = this.tags.map((tag) => ({
              value: tag.text,
              type: tag.type,
            }));
            this.performSearch(terms);
          }
        } else {
          if (this.selectedIndex >= 0 && selectedText) {
            added = this.addTagFromSuggestion(selectedText);
          } else {
            added = this.addTagFromSuggestion(this.searchText.trim());
          }

          if (added) {
            const terms = this.tags.map((tag) => ({
              value: tag.text,
              type: tag.type,
            }));
            this.performSearch(terms);
          }
        }
      }
    },
    toggleDropdown(index) {
      this.activeDropdown = this.activeDropdown === index ? null : index;
    },
    closeDropdown() {
      this.activeDropdown = null;
    },
    validateTag(type, excludeIndex = null) {
      const countSameType = this.tags.filter(
        (tag, idx) => tag.type === type && idx !== excludeIndex
      ).length;
      return countSameType === 0;
    },
    addTagFromSuggestion(text) {
      const tagType = store.getters.getTagType(text);

      if (this.tags.length >= 3) {
        this.addTagError = "You can add up to 3 tags only.";
        return false;
      }

      if (!this.validateTag(tagType)) {
        this.addTagError = `Only one tag allowed for type ${tagType?.toUpperCase()}.`;
        return false;
      }

      const alreadyExists = this.tags.some((tag) => tag.text === text);
      if (alreadyExists) {
        return false;
      }

      store.commit("addTag", { text, type: tagType });
      this.searchText = "";
      this.addTagError = "";
      return true;
    },
    handleTagTypeChange(index, newType) {
      if (!this.validateTag(newType, index)) {
        this.addTagError = `Only one tag allowed for type ${newType?.toUpperCase()}`;
        return;
      }

      this.tags[index].type = newType;
      this.addTagError = "";
      this.closeDropdown();
    },
    removeTag(index) {
      store.commit("removeTag", index);
    },
    toggleFavorite(search) {
      store.commit("toggleFavorite", search);
      this.saveFavourite(search);
    },
    async saveFavourite(search) {
      try {
        let saveFavouriteResponse = await postFetch(
          `users/${this.user_id}/savefavourite`,
          { favouriteSearch: search },
          false,
          true
        );
      } catch (error) {
        console.error("Error while saving favourite:", error);
      }
    },
    isSelected(index) {
      return index === this.selectedIndex;
    },
    getTagIcon(type) {
      const icons = {
        bet_id: "fa-dice bet_id",
        round_id: "fa-clock round_id",
        user_id: "fa-user user_id",
      };
      return icons[type];
    },
    handleClose(val = false) {
      store.commit("setSearchOpen", val);
      store.commit("setSelectedIndex", -1);
      store.commit("clearTags");
    },
    handleGlobalKeydown(e) {
      if (e.ctrlKey && e.key === "k") {
        e.preventDefault();
        this.handleClose(true);
      }
      if (e.key === "Escape") {
        this.handleClose(false);
      }
    },
    searchFromString(searchString) {
      if (!searchString) return;

      const parts = searchString.split(" ").filter(Boolean);

      const terms = parts.map((value) => {
        const type = store.getters.getTagType(value);
        return { type, value };
      });

      this.performSearch(terms);
    },
    formatDateTimeForInput(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    },
    async performSearch(terms) {
      if (terms.length > 0) {
        const searchText = terms.map((t) => t.value).join(" ");
        store.commit("addToRecent", searchText);
        let saveSearch = await postFetch(
          `users/${this.user_id}/saverecent`,
          { recentSearch: searchText },
          false,
          true
        );
      }

      const paramMap = {
        user_id: null,
        round_id: null,
        bet_id: null,
      };

      terms.forEach(({ type, value }) => {
        if (paramMap.hasOwnProperty(type)) {
          paramMap[type] = value;
        }
      });

      const params = {
        skin_id: this.skinFilt,
      };

      if (paramMap.user_id) params.user_id = paramMap.user_id;
      if (paramMap.round_id) params.round_id = paramMap.round_id;
      if (paramMap.bet_id) params.uuid = paramMap.bet_id;

      if (paramMap.user_id) {
        if (
          this.allowedSkins.some(
            (skin) => parseInt(skin.ref_id) === parseInt(paramMap.user_id)
          )
        )
          this.skinFilt = paramMap.user_id.split("_")[0];
      }

      try {
        this.isLoading = true;
        this.noResult = false;
        if (terms.length === 1 && paramMap.user_id) {
          const response = await getFetch(
            `players/detail/${paramMap.user_id}`,
            { skin_id: this.skinFilt }
          );

          if (response && response?._id) {
            const playerId = response._id;
            const lang = this.$route.params.lang || "en";
            const skin = this.skinFilt || this.$route.params.skinFilt || store.getters.skinStorage;
            const skinPath = skin && skin !== "all" ? `/${skin}` : "";
            const targetUrl = `/${lang}${skinPath}/players/${playerId}`;
            if (this.$route.name === "Players") {
              window.location.href = targetUrl;
            } else {
              this.$router.push(targetUrl);
            }

            this.handleClose(false);
            store.commit("clearTags");
          } else {
            this.noResult = true;
          }
        } else {
          const spinParams = {};
          if (paramMap.user_id) spinParams.user_id = paramMap.user_id;
          if (paramMap.round_id) {
            spinParams.round_id = paramMap.round_id;

            //recupero created_at e end_at del round_id cercato

            const multiplierInfo = await await getFetch(`multipliers/`, {
              skin_id: this.skinFilt.trim(),
              roundId: spinParams.round_id,
            });

            if (multiplierInfo.data.length > 0) {
              if (multiplierInfo && multiplierInfo.data.length > 0) {
                spinParams.start_at = this.formatDateTimeForInput(
                  new Date(multiplierInfo.data[0].created_at)
                );

                spinParams.end_at = this.formatDateTimeForInput(
                  new Date(multiplierInfo.data[0].updated_at)
                );
              }
            } else {
              this.noResult = true;
              return;
            }
          }
          if (paramMap.bet_id) spinParams.bet_id = paramMap.bet_id;
          if (!this.isSkinOnStorage) spinParams.skin_id = this.skinFilt;
          // return;

          const queryString = new URLSearchParams(spinParams).toString();
          const fullUrl = `/rounds?${queryString}`;

          setTimeout(() => {
            window.open(fullUrl, "_blank");
          });

          this.handleClose(false);
          store.commit("clearTags");
        }
      } catch (error) {
        this.noResult = true;
        console.error("Error fetching data:", error);
      } finally {
        this.isLoading = false;
        if (this.noResultTimeout) {
          clearTimeout(this.noResultTimeout);
        }

        this.noResultTimeout = setTimeout(() => {
          this.noResult = false;
          this.noResultTimeout = null;
        }, 2500);
      }
    },
  },
  mounted() {
    this.$refs.searchInput?.focus();
    document.body.style.overflow = "hidden";
    this.updateSkinFilt();
    document.addEventListener("click", this.closeDropdown);
    window.addEventListener("keydown", this.handleGlobalKeydown);
  },
  beforeUnmount() {
    document.body.style.overflow = "";
    document.removeEventListener("click", this.closeDropdown);
    window.removeEventListener("keydown", this.handleGlobalKeydown);

    if (this.noResultTimeout) {
      clearTimeout(this.noResultTimeout);
      this.noResultTimeout = null;
    }

    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
      this.errorTimeout = null;
    }
  },
  created() {
    this.fetchLastAndFavourtiesSearch();
  },
  watch: {
    addTagError(newVal) {
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
      }

      if (newVal) {
        this.errorTimeout = setTimeout(() => {
          this.addTagError = "";
          this.errorTimeout = null;
        }, 2500);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../assets/styles/components/globalSearchModal.scss";

.no-result-message {
  color: #dc2626;
  padding: 1rem;
  text-align: center;
  font-weight: 600;
  font-size: 13px;
}

.loader-overlay {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
}

.tutorial-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9876543210;
}

.tutorial-content {
  border-radius: 16px;
  max-width: 800px;
  max-height: 80vh;
  width: 90%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  box-shadow: 0 20px 40px -8px rgba(15, 23, 42, 0.2);

  @media (max-width: 650px) {
    zoom: 80%;
  }
}

.tutorial-header {
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tutorial-header h2 {
  color: #0f172a;
  margin: 0;
  font-size: clamp(16px, 1.1vw, 22px);
  font-weight: 700;

  i {
    color: var(--primary-color, #386f99);
  }
}

.tutorial-close-btn {
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 1.1rem;
  cursor: pointer;
  padding: 6px 10px;
  border-radius: 8px;
  transition: all 0.2s;
}

.tutorial-close-btn:hover {
  color: #0f172a;
  background: #e2e8f0;
}

.tutorial-body {
  padding: 20px;
  overflow: auto;
  overflow-x: hidden;
  background: #ffffff;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.tutorial-step {
  display: flex;
  margin-bottom: 1.25rem;
  align-items: flex-start;
}

.step-number {
  background: var(--primary-color, #386f99);
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  margin-right: 15px;
  flex-shrink: 0;

  @media (max-width: 650px) {
    display: none;
  }
}

.step-content {
  flex: 1;
}

.step-content h3 {
  color: #0f172a;
  margin: 0 0 8px 0;
  font-size: 1rem;
  font-weight: 700;
}

.step-content h3 i {
  margin-right: 8px;
  color: var(--primary-color, #386f99);
}

.step-content p {
  color: #475569;
  margin: 0 0 12px 0;
  line-height: 1.5;
  font-size: 13px;
}

.examples {
  background: #f8fafc;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  border-left: 3px solid var(--primary-color, #386f99);
}

.examples h4 {
  color: #0f172a;
  margin: 0 0 8px 0;
  font-size: 0.85rem;
  font-weight: 700;
}

.example {
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 0px 10px;
  flex-wrap: wrap;
}

.example code {
  background: #e2e8f0;
  color: #0f172a;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}

.example-desc {
  color: #64748b;
  font-size: 0.85rem;
}

.tutorial-footer {
  padding: 14px 20px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.tutorial-btn {
  padding: 8px 16px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  transition: all 0.2s;
}

.tutorial-btn.primary {
  background: var(--primary-color, #386f99);
  color: white;
}

.tutorial-btn.primary:hover {
  background: var(--primary-hover, #2b5577);
}

.tutorial-btn.secondary {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.tutorial-btn.secondary:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.tutorial-btn.small {
  padding: 6px 12px;
  font-size: 0.85rem;
}

.interactive-tutorial-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(15, 23, 42, 0.3);
  z-index: 9876543215;
  pointer-events: none;
}

.tutorial-tooltip {
  position: absolute;
  background: #ffffff;
  border: 1px solid var(--primary-color, #386f99);
  border-radius: 12px;
  padding: 15px;
  max-width: 300px;
  z-index: 9876543210;
  pointer-events: auto;
  box-shadow: 0 10px 25px -5px rgba(15, 23, 42, 0.2);
}

.tooltip-content h4 {
  color: #0f172a;
  margin: 0 0 6px 0;
  font-size: 0.95rem;
  font-weight: 700;
}

.tooltip-content p {
  color: #475569;
  margin: 0 0 12px 0;
  font-size: 0.85rem;
  line-height: 1.4;
}

.tooltip-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.tooltip-arrow {
  position: absolute;
  top: -8px;
  left: 20px;
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid var(--primary-color, #386f99);
}

.wrap-type {
  border-radius: 90px;
  border: 1px solid #e2e8f0;
  justify-content: flex-start;
  gap: 5px;
  padding: 0.35rem 0.75rem;
  margin: 0px 0.15rem;
  background: #f8fafc;
}

.star-yellow {
  color: #f59e0b;
}

.over-info {
  &:hover {
    color: var(--primary-color, #386f99);
    text-decoration: underline;
  }

  cursor: pointer;
  transition: 0.2s all ease;
}

.clear-all-tags-btn {
  width: auto;
  margin-left: auto;
  margin-right: 8px;
  float: right;
  max-width: fit-content;
  padding: 4px 10px;
  border-radius: 8px;
  color: #dc2626;
  font-size: 12px;
  font-weight: 600;
  background: #fef2f2;
  border: 1px solid #fecaca;
  transition: 0.2s all ease;

  &:hover {
    cursor: pointer;
    background: #fee2e2;
  }
}
</style>

<style lang="scss">
.bet_id-tag-type {
  color: #2563eb;
}

.user_id-tag-type {
  color: #d97706;
}

.round_id-tag-type {
  color: #059669;
}

.tag-type-last-favourites-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 5px;
}

.tag-type-last-favourites {
  background: #f8fafc;
  border-radius: 10px;
  padding: 4px 8px;
  font-size: 13px;
  gap: 6px;
  border: 1px solid #e2e8f0;
  color: #334155;

  @media (max-width: 650px) {
    zoom: 80%;
  }
}

.select-custom {
  &.skin-select {
    border-radius: 10px;
    cursor: pointer;
    transition: 0.2s all ease;
    border: 1px solid #cbd5e1;
    background-color: #ffffff;
    color: #334155;
    font-size: 12px;
    font-weight: 600;
    min-width: 80px;
    max-width: 80px;
    width: 80px;
    padding: 3px 6px;

    &:focus {
      outline: none;
      border-color: var(--primary-color, #386f99);
      box-shadow: 0 0 0 2px var(--primary-light, rgba(56, 111, 153, 0.15));
    }
  }
}
</style>
